import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { patch } from "../../lib/api";
import { useSession } from "../../lib/session";
import { DEPOT_NAME_MAX, depotChanges, depotNameError } from "../../lib/station";
import type { Depot } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { Modal } from "../ui/Modal";
import { DepotProvider } from "./DepotProvider";

/**
 * Editing a depot, in a modal over the table: its name, and — under its own
 * permission and its own save — the payment provider.
 *
 * The timezone is shown, not offered. Moving it would move period boundaries that
 * energy figures have already been reported against, and the server refuses it.
 */

export interface EditDepotProps {
  readonly depot: Depot;
  readonly open: boolean;
  readonly onSaved: (depot: Depot) => void;
  /** After the payment provider moves, which saves on its own. */
  readonly onProviderSaved: (message: string) => void;
  readonly onCancel: () => void;
}

export function EditDepot({ depot, open, onSaved, onProviderSaved, onCancel }: EditDepotProps) {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      eyebrow={`Edit depot · ${depot.timezone}`}
      title={depot.name}
      footer={
        <Button variant="ghost" onClick={onCancel}>
          Close
        </Button>
      }
    >
      {/* Mounted only while open, so each opening starts from the depot as it is. */}
      {open ? (
        <EditDepotForms depot={depot} onSaved={onSaved} onProviderSaved={onProviderSaved} />
      ) : null}
    </Modal>
  );
}

function EditDepotForms({
  depot,
  onSaved,
  onProviderSaved,
}: Pick<EditDepotProps, "depot" | "onSaved" | "onProviderSaved">) {
  const queryClient = useQueryClient();
  const { can } = useSession();

  const [name, setName] = useState(depot.name);

  const save = useMutation({
    mutationFn: (body: unknown) => patch<Depot>(`/depots/${encodeURIComponent(depot.id)}`, body),
    onSuccess: (saved) => {
      // The fleet page's depot filter and the station form's picker both read this.
      void queryClient.invalidateQueries({ queryKey: ["depots"] });
      onSaved(saved);
    },
  });

  const body = depotChanges(depot, { name });
  const nameIssue = depotNameError(name);

  return (
    <div className="flex flex-col gap-5">
      {save.isError ? <ErrorNotice error={save.error} /> : null}

      {can("depots.update") ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (body === undefined || save.isPending) return;
            save.mutate(body);
          }}
          aria-label={`Rename ${depot.name}`}
          className="flex flex-col gap-4"
        >
          <Field
            label="Site name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={DEPOT_NAME_MAX}
            required
            autoFocus
            {...(nameIssue === undefined ? {} : { error: nameIssue })}
          />

          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="submit"
              variant="primary"
              disabled={body === undefined || save.isPending}
              pending={save.isPending}
            >
              Save depot
            </Button>
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              Timezone {depot.timezone} — fixed once stations report against it.
            </span>
          </div>
        </form>
      ) : null}

      {can("payment_providers.manage") ? (
        <DepotProvider depot={depot} onSaved={onProviderSaved} />
      ) : null}
    </div>
  );
}
